/**
 * Local-only spellbooks (IndexedDB) for unauthenticated demo mode.
 * Uses negative integer ids so they never collide with server spellbook ids.
 */

import { normalizeSpellSlots } from '@/utils/spellbook';

const DB_NAME = 'mythkit_spellbooks_local';
const DB_VERSION = 1;
const STORE = 'books';

let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB not available'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
  });
  return dbPromise;
}

/** Negative id, unique enough for client-only rows */
export function generateLocalSpellbookId() {
  const base = Date.now() % 2000000000;
  const jitter = Math.floor(Math.random() * 999999);
  return -(base + jitter);
}

/**
 * @returns {Promise<any[]>}
 */
export async function getAll() {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => resolve((req.result || []).map(normalizeRow));
      req.onerror = () => reject(req.error);
    });
  } catch {
    return [];
  }
}

/**
 * @param {number} id
 * @returns {Promise<any|null>}
 */
export async function getById(id) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(Number(id));
      req.onsuccess = () => resolve(req.result ? normalizeRow(req.result) : null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

function normalizeRow(row) {
  if (!row || typeof row !== 'object') return row;
  return {
    ...row,
    id: Number(row.id),
    spell_slots: normalizeSpellSlots(row.spell_slots || {}),
    spells: Array.isArray(row.spells) ? row.spells : [],
    cast_history: Array.isArray(row.cast_history) ? row.cast_history : [],
  };
}

/**
 * @param {object} book
 * @returns {Promise<any>}
 */
export async function put(book) {
  const id = book.id != null ? Number(book.id) : generateLocalSpellbookId();
  const now = new Date().toISOString();
  const doc = normalizeRow({
    ...book,
    id,
    spells: Array.isArray(book.spells) ? book.spells : [],
    cast_history: Array.isArray(book.cast_history) ? book.cast_history : [],
    spell_slots: normalizeSpellSlots(book.spell_slots || {}),
    updated_at: book.updated_at || now,
    created_at: book.created_at || now,
  });
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).put(doc);
    req.onsuccess = () => resolve(doc);
    req.onerror = () => reject(req.error);
  });
}

/**
 * @param {number} id
 * @returns {Promise<void>}
 */
export async function remove(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).delete(Number(id));
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

/**
 * Clear all local spellbooks (e.g. after successful migration to server).
 * @returns {Promise<void>}
 */
export async function clearAll() {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const req = tx.objectStore(STORE).clear();
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch {
    // ignore
  }
}
